<template>
  <div
    v-if="shouldShow"
    class="trial-footer"
    :style="{
      left: `${contentOffsetLeft}px`,
      width: `calc(100% - ${contentOffsetLeft}px)`,
    }"
  >
    <div class="trial-footer__content">
      <VIcon icon="tabler-alert-circle" color="primary" class="me-2" />
      <span class="me-3">
        Seu teste termina em
        <strong>{{ formattedEndDate }}</strong>
      </span>
      <RouterLink to="/plans/selection">
        <VBtn color="primary" size="small">assinar</VBtn>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { usePlansStore } from "@/store/plans";
import { userStore } from "@/store/user";

type Subscription = {
  status: string;
  cycleEndDate?: string;
};

const plansStore = usePlansStore();
const user = userStore();

const subscription = ref<Subscription | null>(null);
const contentOffsetLeft = ref(0);

const formattedEndDate = computed(() => {
  if (!subscription.value?.cycleEndDate) return "";
  // Usar UTC para evitar problemas de timezone na exibição
  const date = new Date(subscription.value.cycleEndDate);
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
});

const shouldShow = computed(() => {
  if (!subscription.value) return false;
  const status = String(subscription.value.status || "").toUpperCase();
  const isTrial = status.includes("TRIAL");
  if (!isTrial) return false;
  if (!subscription.value.cycleEndDate) return true;
  return new Date(subscription.value.cycleEndDate) >= new Date();
});

// Calcular deslocamento do conteúdo para alinhar o footer
const updateOffset = () => {
  try {
    const wrapper = document.querySelector(
      ".layout-wrapper.layout-nav-type-vertical .layout-content-wrapper"
    ) as HTMLElement | null;
    if (wrapper && window.innerWidth >= 1280) {
      const px = wrapper.getBoundingClientRect().left;
      contentOffsetLeft.value = Number.isFinite(px) ? px : 0;
    } else {
      contentOffsetLeft.value = 0;
    }
  } catch (_) {
    contentOffsetLeft.value = 0;
  }
};

let observer: MutationObserver | null = null;

onMounted(async () => {
  try {
    const companyId = user.user?.companyId;
    if (!companyId) return;
    const current = await plansStore.fetchCurrentSubscription(companyId);
    subscription.value = current;
  } catch (e) {
    // silenciosamente não exibe o banner em caso de erro
    // 404 é esperado se a empresa não tem assinatura
  }

  updateOffset();
  window.addEventListener("resize", updateOffset);

  const wrapperEl = document.querySelector(
    ".layout-wrapper.layout-nav-type-vertical"
  );
  if (wrapperEl) {
    observer = new MutationObserver(updateOffset);
    observer.observe(wrapperEl, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateOffset);
  observer?.disconnect();
});
</script>

<style scoped>
.trial-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  /* Fica acima do conteúdo, mas abaixo do sidebar (z-index do sidebar ≈ 12) */
  z-index: 8;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.trial-footer__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  color: rgb(var(--v-theme-primary));
}

/* O posicionamento horizontal é controlado via style binding para seguir o layout */
</style>
